import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginStart, loginSuccess, loginFailure } from '../store/slices/authSlice';
import { RootState } from '../store';
import { authService } from '../services/authService';
import ArthLogo from '../assets/logo.png';

const useParallax = (ref: React.RefObject<HTMLDivElement | null>) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (ref.current) {
        const { clientWidth, clientHeight } = document.documentElement;
        const moveX = (e.clientX - clientWidth / 2) / 40;
        const moveY = (e.clientY - clientHeight / 2) / 40;
        setPosition({ x: moveX, y: moveY });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [ref]);

  useEffect(() => {
    if (ref.current) {
      ref.current.style.transform = `translate(${position.x}px, ${position.y}px)`;
      ref.current.style.transition = 'transform 0.1s linear';
    }
  }, [position, ref]);
};

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<'login' | 'change-password' | 'otp' | 'forgot-password'>('login');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [cpError, setCpError] = useState('');
  const [cpSuccess, setCpSuccess] = useState('');

  // 2FA OTP States
  const [tempToken, setTempToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpSuccess, setOtpSuccess] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  // Forgot Password States
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [showCpCurrent, setShowCpCurrent] = useState(false);
  const [showCpNew, setShowCpNew] = useState(false);
  const [showCpConfirm, setShowCpConfirm] = useState(false);

  const visualRef = useRef<HTMLDivElement>(null);
  useParallax(visualRef);

  const orbitParticles = [
    { angle: 0, color: '#473192', size: 8, speed: 11, direction: 'reverse', radius: 195 },
    { angle: 72, color: '#5e43b8', size: 6, speed: 7, direction: 'normal', radius: 205 },
    { angle: 144, color: '#473192', size: 7, speed: 7, direction: 'reverse', radius: 120 },
    { angle: 216, color: '#5e43b8', size: 5, speed: 15, direction: 'normal', radius: 130 },
    { angle: 288, color: '#473192', size: 10, speed: 20, direction: 'reverse', radius: 215 },
  ];

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { error } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const switchMode = (next: 'login' | 'change-password' | 'otp' | 'forgot-password') => {
    setMode(next);
    setUsername('');
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setCpError('');
    setCpSuccess('');
    setTempToken('');
    setOtpCode('');
    setOtpError('');
    setOtpSuccess('');
    setForgotEmail('');
    setForgotError('');
    setForgotSuccess('');
    // Clear login errors in store
    dispatch(loginFailure(null));
  };

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    dispatch(loginStart());
    try {
      const response = await authService.login(username, password);
      
      if (response.require_2fa) {
        setTempToken(response.temp_token);
        setMode('otp');
        setOtpCode('');
        setOtpError('');
        setOtpSuccess('');
        setResendTimer(60);
        dispatch(loginFailure(null));
        return;
      }

      localStorage.setItem('access_token', response.access);
      localStorage.setItem('refresh_token', response.refresh);

      const userDetails = await authService.getMe();

      dispatch(loginSuccess({
        id: userDetails.id,
        username: userDetails.user_name,
        email: userDetails.email,
        first_name: userDetails.first_name,
        last_name: userDetails.last_name,
        role: userDetails.role || 'user',
        department: userDetails.department || 'Operations',
        status: userDetails.status,
        platform: userDetails.platform,
      }));

      navigate(`/${userDetails.platform}`);
    } catch (err: any) {
      dispatch(loginFailure(err.message || 'Login failed'));
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError('');
    setOtpSuccess('');
    dispatch(loginStart());
    try {
      const tokens = await authService.verifyOtp(tempToken, otpCode);
      localStorage.setItem('access_token', tokens.access);
      localStorage.setItem('refresh_token', tokens.refresh);

      const userDetails = await authService.getMe();

      dispatch(loginSuccess({
        id: userDetails.id,
        username: userDetails.user_name,
        email: userDetails.email,
        first_name: userDetails.first_name,
        last_name: userDetails.last_name,
        role: userDetails.role || 'user',
        department: userDetails.department || 'Operations',
        status: userDetails.status,
        platform: userDetails.platform,
      }));

      navigate(`/${userDetails.platform}`);
    } catch (err: any) {
      setOtpError(err.message || 'OTP verification failed');
      dispatch(loginFailure(err.message || 'OTP verification failed'));
    }
  };

  const handleResendOtp = async () => {
    setOtpError('');
    setOtpSuccess('');
    try {
      await authService.resendOtp(tempToken);
      setOtpSuccess('A new OTP has been sent to your registered email.');
      setResendTimer(60);
    } catch (err: any) {
      setOtpError(err.message || 'Failed to resend OTP');
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotSuccess('');
    try {
      await authService.forgotPassword(forgotEmail);
      setForgotSuccess('If the account matches a registered active user, a password reset link has been sent to your email.');
      setForgotEmail('');
    } catch (err: any) {
      setForgotError(err.message || 'Failed to request password reset');
    }
  };

  const handleChangePassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCpError('');
    setCpSuccess('');

    if (newPassword !== confirmPassword) {
      setCpError('New passwords do not match');
      return;
    }

    try {
      const tokens = await authService.login(username, password);
      localStorage.setItem('access_token', tokens.access);
      localStorage.setItem('refresh_token', tokens.refresh);

      await authService.changePassword(password, newPassword);
      await authService.logout();

      setCpSuccess('Password changed successfully. Please sign in with your new password.');
      setUsername('');
      setPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      await authService.logout();
      setCpError(err.message || 'Failed to change password');
    }
  };


  return (
    <div className="login-split-layout">
      <div className="login-visual-panel">
        <div ref={visualRef} className="parallax-layer" style={{ width: '100%', height: '100%', position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>

          {/* Spinning Orbits */}
          <div className="spinning-orbits">
            <div className="orbit-path slow">
              <svg width="380" height="380" viewBox="0 0 380 380">
                <circle cx="190" cy="190" r="190" fill="none" stroke="rgba(71, 49, 146, 0.4)" strokeWidth="3" strokeDasharray="8 6" />
              </svg>
            </div>
            <div className="orbit-path reverse">
              <svg width="260" height="260" viewBox="0 0 260 260">
                <circle cx="130" cy="130" r="130" fill="none" stroke="rgba(71, 49, 146, 0.6)" strokeWidth="2.5" strokeDasharray="4 4" />
              </svg>
            </div>
          </div>

          {/* Orbiting Particles */}
          {orbitParticles.map((p, i) => (
            <div
              key={i}
              className="particle-orbit-anchor"
              style={{
                transform: `rotate(${p.angle}deg)`,
              }}
            >
              <div
                className={`particle-animator ${p.direction === 'reverse' ? 'direction-reverse' : ''}`}
                style={{
                  animationDuration: `${p.speed}s`,
                  animationDelay: `${i * 0}s`,
                  '--orbit-radius': `${p.radius}px`,
                } as any}
              >
                <div
                  className="particle-circle"
                  style={{
                    width: `${p.size}px`,
                    height: `${p.size}px`,
                    backgroundColor: p.color,
                    boxShadow: `0 0 8px 2px ${p.color}88`,
                    borderRadius: '50%',
                    opacity: 0.8
                  }}
                />
              </div>
            </div>
          ))}

          <div className="logo-container" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
            <img src={ArthLogo} alt="Logo" style={{ width: '100px' }} />
          </div>

          <div className="login-visual-content" style={{ zIndex: 10, position: 'absolute', bottom: '10%', textAlign: 'center', width: '100%' }}>

            <p style={{ marginTop: '0.5rem', fontSize: '1.125rem', color: 'var(--primary-light)', margin: 0 }}>&copy; {new Date().getFullYear()} ARTH</p>
          </div>
        </div>
      </div>
      <div className="login-form-panel">
        <h2>
          {mode === 'login' && 'Welcome'}
          {mode === 'change-password' && 'Change Password'}
          {mode === 'otp' && 'Two-Factor Verification'}
          {mode === 'forgot-password' && 'Forgot Password'}
        </h2>
        <p>
          {mode === 'login' && 'Please enter your details'}
          {mode === 'change-password' && 'Update your account password'}
          {mode === 'otp' && 'Please enter the 6-digit code sent to your email'}
          {mode === 'forgot-password' && 'We will send you a link to reset your password'}
        </p>

        {mode === 'login' && (
          <form onSubmit={handleLogin} style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
            {error && <div style={{ color: 'red', marginBottom: '1rem', textAlign: 'center', backgroundColor: '#fee2e2', padding: '0.5rem', borderRadius: '4px', fontSize: '0.85rem' }}>{error}</div>}

            <div className="floating-input-group">
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder=" "
                required
              />
              <label htmlFor="username">Username</label>
            </div>

            <div className="floating-input-group" style={{ position: 'relative' }}>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder=" "
                required
                style={{ paddingRight: '2.5rem' }}
              />
              <label htmlFor="password">Password</label>
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-light)', padding: 0 }}
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                ) : (
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                )}
              </button>
            </div>

            <div className="form-actions" style={{ marginBottom: '1rem' }}>
              <a href="#" className="forgot-password" onClick={(e) => { e.preventDefault(); switchMode('forgot-password'); }}>
                Forgot Password?
              </a>
              <a href="#" className="forgot-password" onClick={(e) => { e.preventDefault(); switchMode('change-password'); }}>
                Change password
              </a>
            </div>

            <button type="submit" className="primary-btn">
              <span>Sign In</span>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </form>
        )}

        {mode === 'change-password' && (
          <form onSubmit={handleChangePassword} style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
            {cpError && <div style={{ color: 'red', marginBottom: '1rem', textAlign: 'center', backgroundColor: '#fee2e2', padding: '0.5rem', borderRadius: '4px', fontSize: '0.85rem' }}>{cpError}</div>}
            {cpSuccess && <div style={{ color: '#15803d', marginBottom: '1rem', textAlign: 'center', backgroundColor: '#dcfce7', padding: '0.5rem', borderRadius: '4px', fontSize: '0.85rem' }}>{cpSuccess}</div>}

            <div className="floating-input-group">
              <input
                type="text"
                id="cp-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder=" "
                required
              />
              <label htmlFor="cp-username">Username</label>
            </div>

            <div className="floating-input-group" style={{ position: 'relative' }}>
              <input
                type={showCpCurrent ? "text" : "password"}
                id="cp-current"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder=" "
                required
                style={{ paddingRight: '2.5rem' }}
              />
              <label htmlFor="cp-current">Current Password</label>
              <button
                type="button"
                onClick={() => setShowCpCurrent(!showCpCurrent)}
                style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-light)', padding: 0 }}
                tabIndex={-1}
              >
                {showCpCurrent ? (
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                ) : (
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                )}
              </button>
            </div>

            <div className="floating-input-group" style={{ position: 'relative' }}>
              <input
                type={showCpNew ? "text" : "password"}
                id="cp-new"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder=" "
                required
                style={{ paddingRight: '2.5rem' }}
              />
              <label htmlFor="cp-new">New Password</label>
              <button
                type="button"
                onClick={() => setShowCpNew(!showCpNew)}
                style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-light)', padding: 0 }}
                tabIndex={-1}
              >
                {showCpNew ? (
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                ) : (
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                )}
              </button>
            </div>

            <div className="floating-input-group" style={{ position: 'relative' }}>
              <input
                type={showCpConfirm ? "text" : "password"}
                id="cp-confirm"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder=" "
                required
                style={{ paddingRight: '2.5rem' }}
              />
              <label htmlFor="cp-confirm">Confirm New Password</label>
              <button
                type="button"
                onClick={() => setShowCpConfirm(!showCpConfirm)}
                style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-light)', padding: 0 }}
                tabIndex={-1}
              >
                {showCpConfirm ? (
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                ) : (
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                )}
              </button>
            </div>

            <button type="submit" className="primary-btn">
              <span>Update Password</span>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
            <button
              type="button"
              className="secondary-btn"
              style={{ width: '100%', marginTop: '1rem', borderRadius: '9999px', justifyContent: 'center' }}
              onClick={() => switchMode('login')}
            >
              Back to Sign In
            </button>
          </form>
        )}

        {mode === 'otp' && (
          <form onSubmit={handleVerifyOtp} style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
            {otpError && <div style={{ color: 'red', marginBottom: '1rem', textAlign: 'center', backgroundColor: '#fee2e2', padding: '0.5rem', borderRadius: '4px', fontSize: '0.85rem' }}>{otpError}</div>}
            {otpSuccess && <div style={{ color: '#15803d', marginBottom: '1rem', textAlign: 'center', backgroundColor: '#dcfce7', padding: '0.5rem', borderRadius: '4px', fontSize: '0.85rem' }}>{otpSuccess}</div>}

            <div className="floating-input-group">
              <input
                type="text"
                id="otp-code"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder=" "
                required
                maxLength={6}
              />
              <label htmlFor="otp-code">6-Digit OTP Code</label>
            </div>

            <button type="submit" className="primary-btn" style={{ width: '100%', justifyContent: 'center' }}>
              <span>Verify OTP</span>
            </button>

            <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
              <button
                type="button"
                className="secondary-btn"
                disabled={resendTimer > 0}
                onClick={handleResendOtp}
                style={{ fontSize: '0.9rem', color: resendTimer > 0 ? 'var(--text-light)' : 'var(--primary)' }}
              >
                {resendTimer > 0 ? `Resend OTP (${resendTimer}s)` : 'Resend OTP'}
              </button>
            </div>
            
            <button
              type="button"
              className="secondary-btn"
              style={{ width: '100%', marginTop: '1rem', borderRadius: '9999px', justifyContent: 'center' }}
              onClick={() => switchMode('login')}
            >
              Back to Sign In
            </button>
          </form>
        )}

        {mode === 'forgot-password' && (
          <form onSubmit={handleForgotPassword} style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
            {forgotError && <div style={{ color: 'red', marginBottom: '1rem', textAlign: 'center', backgroundColor: '#fee2e2', padding: '0.5rem', borderRadius: '4px', fontSize: '0.85rem' }}>{forgotError}</div>}
            {forgotSuccess && <div style={{ color: '#15803d', marginBottom: '1rem', textAlign: 'center', backgroundColor: '#dcfce7', padding: '0.5rem', borderRadius: '4px', fontSize: '0.85rem' }}>{forgotSuccess}</div>}

            <div className="floating-input-group">
              <input
                type="email"
                id="forgot-email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder=" "
                required
              />
              <label htmlFor="forgot-email">Email Address</label>
            </div>

            <button type="submit" className="primary-btn" style={{ width: '100%', justifyContent: 'center' }}>
              <span>Send Reset Link</span>
            </button>

            <button
              type="button"
              className="secondary-btn"
              style={{ width: '100%', marginTop: '1.5rem', borderRadius: '9999px', justifyContent: 'center' }}
              onClick={() => switchMode('login')}
            >
              Back to Sign In
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;
